import { Chip, Skeleton, Tooltip } from "@mui/material";
import { classes } from "common/theme/theme";
import React, {
  CSSProperties,
  FC,
  ReactElement,
  Ref,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

const DEFAULT_CHIP_GAP_PX = 4;
const MIN_TRUNCATED_CHIP_WIDTH_PX = 48;
const LOADING_CHIP_SIZE_PX = {
  width: 72,
  height: 24,
};

export interface ListValue {
  id: string;
  name: string;
  // rendered after the name; when the chip truncates, the name elides and the
  // suffix stays visible
  suffix?: string;
}

export const listValueText = ({ name, suffix }: ListValue): string =>
  suffix === undefined ? name : `${name}${suffix}`;

export interface CarbonListCellProps {
  values: ListValue[];
  icon?: ReactElement;
  loading?: boolean;
  gapPx?: number;
}

export const CarbonListCell: FC<CarbonListCellProps> = ({
  values,
  icon,
  loading = false,
  gapPx = DEFAULT_CHIP_GAP_PX,
}) => {
  const measureRef = useRef<HTMLDivElement>(null);
  const chipContainerRef = useRef<HTMLDivElement>(null);
  const [overflowCount, setOverflowCount] = useState(0);
  const [truncateFirstChip, setTruncateFirstChip] = useState(false);
  const chipRowStyle: CSSProperties = { gap: gapPx };

  // Recalculate the overflow count when the container or measure container changes
  const recalculateOverflow = useCallback(() => {
    const container = chipContainerRef.current;
    const measureContainer = measureRef.current;
    if (!container || !measureContainer || values.length === 0) {
      setOverflowCount(0);
      setTruncateFirstChip(false);
      return;
    }

    const chipElements = [
      ...measureContainer.querySelectorAll<HTMLElement>("[data-measure-chip]"),
    ];
    const badgeElement = measureContainer.querySelector<HTMLElement>(
      "[data-measure-badge]"
    );
    const badgeWidth = badgeElement?.offsetWidth ?? 0;
    const availableWidth = container.clientWidth;

    let usedWidth = 0;
    let visibleCount = 0;
    for (let index = 0; index < chipElements.length; index++) {
      const chipWidth = chipElements[index]?.offsetWidth ?? 0;
      const hasHiddenChips = index < chipElements.length - 1;
      const reservedWidth = hasHiddenChips ? badgeWidth + gapPx : 0;
      if (usedWidth + chipWidth + reservedWidth > availableWidth) {
        break;
      }
      usedWidth += chipWidth + gapPx;
      visibleCount++;
    }

    const truncatedChipSpace = availableWidth - badgeWidth - gapPx;
    const hiddenBeforeMinWidthCheck = values.length - visibleCount;

    if (
      values.length > 1 &&
      hiddenBeforeMinWidthCheck > 0 &&
      visibleCount <= 1 &&
      truncatedChipSpace < MIN_TRUNCATED_CHIP_WIDTH_PX
    ) {
      setOverflowCount(values.length);
      setTruncateFirstChip(false);
      return;
    }

    if (visibleCount === 0 && chipElements.length > 0) {
      visibleCount = 1;
    }

    const nextOverflowCount = values.length - visibleCount;
    const firstChipWidth = chipElements[0]?.offsetWidth ?? 0;
    const shouldTruncateFirst =
      nextOverflowCount > 0 &&
      visibleCount === 1 &&
      firstChipWidth > truncatedChipSpace;

    setOverflowCount(nextOverflowCount);
    setTruncateFirstChip(shouldTruncateFirst);
  }, [gapPx, values]);

  useLayoutEffect(() => {
    if (loading) {
      setOverflowCount(0);
      setTruncateFirstChip(false);
      return;
    }

    recalculateOverflow();

    const container = chipContainerRef.current;
    if (!container) {
      return;
    }

    const observer = new ResizeObserver(() => {
      recalculateOverflow();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [loading, recalculateOverflow]);

  if (values.length === 0) {
    return <></>;
  }

  if (loading) {
    return (
      <ChipRow style={chipRowStyle}>
        {values.map((value) => (
          <Skeleton
            key={value.id}
            variant="rounded"
            className="shrink-0 rounded-full"
            {...LOADING_CHIP_SIZE_PX}
          />
        ))}
      </ChipRow>
    );
  }

  const badgeOnly = overflowCount === values.length && values.length > 1;
  const visibleCount = values.length - overflowCount;
  let visibleValues = values;
  if (badgeOnly) {
    visibleValues = [];
  } else if (overflowCount > 0) {
    visibleValues = values.slice(0, visibleCount);
  }

  let hiddenValues: ListValue[] = [];
  if (badgeOnly) {
    hiddenValues = values;
  } else if (overflowCount > 0) {
    hiddenValues = values.slice(visibleCount);
  }

  return (
    <>
      <ChipRow chipContainerRef={measureRef} ariaHidden style={chipRowStyle}>
        {values.map((value) => (
          <ListValueChip key={value.id} value={value} icon={icon} measure />
        ))}
        <OverflowCountChip count={values.length} measure />
      </ChipRow>
      <ChipRow chipContainerRef={chipContainerRef} style={chipRowStyle}>
        {visibleValues.map((value, index) => (
          <ListValueChip
            key={value.id}
            value={value}
            icon={icon}
            truncate={truncateFirstChip && index === 0}
          />
        ))}
        {overflowCount > 0 ? (
          <OverflowCountChip
            count={overflowCount}
            hiddenNames={hiddenValues.map((value) => listValueText(value))}
          />
        ) : undefined}
      </ChipRow>
    </>
  );
};

const ChipRow: FC<{
  chipContainerRef?: Ref<HTMLDivElement>;
  ariaHidden?: boolean;
  style?: CSSProperties;
  children: React.ReactNode;
}> = ({ chipContainerRef, ariaHidden, style, children }) => (
  <div
    ref={chipContainerRef}
    aria-hidden={ariaHidden}
    style={style}
    className={classes(
      "flex items-center",
      ariaHidden
        ? "invisible absolute pointer-events-none top-0 left-0"
        : "min-w-0 overflow-hidden h-full w-full"
    )}
  >
    {children}
  </div>
);

const ListValueChip: FC<{
  value: ListValue;
  icon?: ReactElement;
  measure?: boolean;
  truncate?: boolean;
}> = ({ value, icon, measure, truncate = false }) => (
  <Chip
    {...(measure ? { "data-measure-chip": true } : {})}
    label={
      value.suffix === undefined ? (
        value.name
      ) : (
        <>
          <span className="truncate">{value.name}</span>
          <span className="shrink-0">{value.suffix}</span>
        </>
      )
    }
    icon={icon}
    size="small"
    className={classes(
      measure || !truncate ? "shrink-0" : "min-w-0 max-w-full flex-1 shrink",
      value.suffix === undefined
        ? truncate && "[&_.MuiChip-label]:truncate"
        : "[&_.MuiChip-label]:flex [&_.MuiChip-label]:items-center [&_.MuiChip-label]:gap-1"
    )}
  />
);

const OverflowCountChip: FC<{
  count: number;
  measure?: boolean;
  hiddenNames?: string[];
}> = ({ count, measure, hiddenNames }) => {
  const chip = (
    <Chip
      {...(measure ? { "data-measure-badge": true } : {})}
      label={`+${count}`}
      size="small"
      className="shrink-0"
    />
  );

  if (measure || !hiddenNames?.length) {
    return chip;
  }

  return (
    <Tooltip
      placement="top"
      title={
        <span className="whitespace-pre-line">{hiddenNames.join("\n")}</span>
      }
    >
      {chip}
    </Tooltip>
  );
};
